import * as Sentry from '@sentry/browser'
import axios from 'axios'
import axiosRetry from 'axios-retry'

import {
  BackendAPIError,
  BadRequestError,
  NoResponseError,
  ServerFailureError,
  isErrorResponse,
} from '@/api/errors'
import { getVersion } from '@/api/version'
import { useSettingStore } from '@/stores/setting'
import { enhanceError } from '@/utils/errors'
import loc from '@/utils/loc'
import { authRedirect } from '@/utils/utils'

// A deliberate subset of the backend's IGNORED_ERROR_CODES, not a mirror of it: a code earns a spot
// here only once the UI presents that failure itself, as the Media tab does for a throttled recheck.
const UNREPORTED_ERROR_CODES = new Set(['throttled'])

export async function handleBrowserAxiosError(error) {
  if (!axios.isAxiosError(error)) {
    Sentry.captureException(new Error('Axios had an exception not an AxiosError', { cause: error }))
  } else {
    const url = error.config?.url
    Sentry.setContext('axiosConfig', error.config ?? null)
    Sentry.setContext('axiosRequest', error.request || {})
    if (!error.response) {
      Sentry.captureException(
        new NoResponseError(`Recieved no response in api call to ${url}`, {
          cause: error,
        }),
      )
    } else {
      const status_code = error.response.status
      Sentry.setContext('axiosResponse', error.response)
      if (!isErrorResponse(error.response)) {
        Sentry.captureException(
          new ServerFailureError(`Received ill-formated ${status_code} response from ${url}`, {
            cause: error,
          }),
        )
      } else {
        Sentry.setContext('ttError', error.response.data.error)

        // Standardize our handling of API responses so code above can easily tell the difference between
        // the BE rejecting an invalid request and something going wrong in typescript code,
        error = enhanceError(error, error.response.data.error)
        if (status_code == 400) {
          Sentry.captureException(new BadRequestError(`${error.code} in ${url}`, { cause: error }))
        } else if (status_code == 401 || status_code == 403) {
          const location = loc.location()
          return authRedirect(location.pathname + location.search)
        } else if (status_code >= 500) {
          Sentry.captureException(
            new ServerFailureError(`Recieved ${status_code} in api call to ${url}`, {
              cause: error,
            }),
          )
        } else if (UNREPORTED_ERROR_CODES.has(error.code)) {
          // Not an issue, but still a trail: a caller that regresses into hammering a throttled
          // endpoint should be visible on whatever event does get reported next.
          Sentry.addBreadcrumb({
            category: 'http',
            level: 'info',
            message: `Handled ${status_code}-${error.code} in ${url}`,
          })
        } else {
          Sentry.captureException(
            new BackendAPIError(`Handled ${status_code}-${error.code} in ${url}`, {
              cause: error,
            }),
          )
        }
      }
    }
  }
  return Promise.reject(error)
}

export function configureBrowserAxios() {
  axiosRetry(axios, { retries: 2, retryDelay: axiosRetry.exponentialDelay })
  axios.interceptors.response.use(response => {
    useSettingStore().updateVenueAppVersion(response.headers['x-tt-version'])
    return response
  }, handleBrowserAxiosError)

  axios.defaults.xsrfHeaderName = 'X-CSRFToken'
  axios.defaults.xsrfCookieName = 'csrftoken'
  axios.defaults.headers['X-TT-ClientVersion'] = getVersion()
}
